/**
 * useShopProducts — PUBLIC storefront list viewmodel.
 *
 * Drives /shop with search + multi-category + price range + per-CDT
 * attribute filters. Fetches via plain `fetch` (no auth context — the
 * endpoint is anonymous) and exposes `setFilters` so the page updates
 * the URL `?q=...&categoryIds=...` etc. and reload the list.
 *
 * Pagination is handled here (not via usePaginatedList) because the
 * fetcher is anonymous and we don't share the SOP-02 mutation
 * primitives this list never needs.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES } from '../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

export interface ShopProductCardDto {
  readonly id: string
  readonly name: string
  readonly category_id: string
  readonly category_name: string
  readonly min_price_paisa: number | null
  readonly max_price_paisa: number | null
  readonly total_on_hand: number
  readonly thumbnail_media_id: string | null
}

export interface ShopProductsFilter {
  readonly q: string
  readonly categoryIds: ReadonlyArray<string>
  readonly priceMinPaisa: number | null
  readonly priceMaxPaisa: number | null
  /** attribute_id → array of selected CDT value ids. */
  readonly attributeValues: Readonly<Record<string, ReadonlyArray<string>>>
}

export const EMPTY_FILTER: ShopProductsFilter = {
  q: '',
  categoryIds: [],
  priceMinPaisa: null,
  priceMaxPaisa: null,
  attributeValues: {}
}

const DEFAULT_PAGE_SIZE = 24

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: ShopProductCardDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

function buildUrl(filter: ShopProductsFilter, page: number, pageSize: number): string {
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('pageSize', String(pageSize))
  if (filter.q.trim().length > 0) params.set('q', filter.q.trim())
  if (filter.categoryIds.length > 0) params.set('categoryIds', filter.categoryIds.join(','))
  if (filter.priceMinPaisa !== null) params.set('priceMinPaisa', String(filter.priceMinPaisa))
  if (filter.priceMaxPaisa !== null) params.set('priceMaxPaisa', String(filter.priceMaxPaisa))
  for (const [attrId, vals] of Object.entries(filter.attributeValues)) {
    if (vals.length === 0) continue
    params.set(`attr_${attrId}`, vals.join(','))
  }
  return `${APIBASE}${APIROUTES.SHOPPRODUCTS}?${params.toString()}`
}

export interface UseShopProductsReturn {
  readonly items: ShopProductCardDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
}

export function useShopProducts(filter: ShopProductsFilter): UseShopProductsReturn {
  const [items, setItems] = useState<ShopProductCardDto[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = DEFAULT_PAGE_SIZE
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)

  // Filter changes reset the page back to 1 so the user always sees
  // the top of the new result set.
  const filterRef = useRef(filter)
  useEffect(() => {
    if (filterRef.current !== filter) {
      filterRef.current = filter
      setPage(1)
    }
  }, [filter])

  useEffect(() => {
    const ac = new AbortController()
    let cancelled = false
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        let res: Response
        try {
          res = await fetch(buildUrl(filter, page, pageSize), { signal: ac.signal })
        } catch (err) {
          if (cancelled || ac.signal.aborted) return
          if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
          throw err
        }
        if (cancelled || ac.signal.aborted) return
        if (res.status >= 500 || !res.ok) {
          throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        }
        const body = (await res.json()) as ListBody
        if (!cancelled && !ac.signal.aborted) {
          setItems(body.data.items)
          setTotal(body.data.total)
        }
      } catch (e) {
        if (cancelled || ac.signal.aborted) return
        setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
        setItems([])
        setTotal(0)
      } finally {
        if (!cancelled && !ac.signal.aborted) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
      ac.abort()
    }
  }, [filter, page, pageSize, reloadTick])

  const reload = useCallback(() => setReloadTick((t) => t + 1), [])

  return { items, total, page, pageSize, isLoading, error, setPage, reload }
}
